import React from 'react';
import { Card, OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './bloodwork-anemia-status.css';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import { FurtherConsiderations } from '../../models/FurtherConsiderations';
import { analyzeMvx } from '../../models/MetabolicVulnerability';
import { resolveBlueprintSteps, BlueprintMarkerChip, BlueprintStepResult } from '../../models/mvxBlueprint';
import { MVX_STEP_SUPPLEMENTS, MvxSupplement } from '../../models/mvxSupplements';
import { inputsFromBloodwork } from './metabolic-vulnerability-insights';
import { renderArrows } from './further-considerations-section';

/**
 * 7-Step Blueprint to Optimal Health (Darren Schmidt, DC) — the guidance layer on
 * top of the MVX panel. Renders his 7 steps as a HORIZONTAL row of numbered boxes
 * (same idiom as the Supplements Protocol panel's kit cards) and HIGHLIGHTS the
 * steps this patient's MVX factor states point to. Every box carries a chip per
 * marker the step reads: the ones lighting it in the step's color, the optimal
 * ones as a muted "= Marker", so a dimmed box still says what would light it.
 * Routing lives in the model (`resolveBlueprintSteps`); this is presentation.
 * Products per step come from `mvxSupplements.ts` (Schmidt's own lists).
 */
interface Props {
  bloodworkAnalysis: BloodworkAnalysis;
  // When provided, product links come from the supplement catalog (the sheet's
  // exact URLs, via supplementLinkByName); otherwise the model's fallback URL.
  furtherConsiderations?: FurtherConsiderations;
}

// Distinct per-step colors, in the same muted family as the Supplements Protocol
// kit cards (teal / green / gold / purple / brown / blue / rose).
const STEP_COLORS = ['#2f6f7e', '#3f7a55', '#9c6a1e', '#6f4f89', '#965a34', '#3f6ea6', '#a24b5a'];

// One marker chip. A driver in a lit box takes the step's color; an optimal
// "= Marker" chip, or any chip in a dimmed box, is muted. The model writes the
// state as ▲ / ▼ / = and renderArrows swaps the triangles for the same
// FontAwesome arrows the rest of the report draws.
const markerChip = (chip: BlueprintMarkerChip, on: boolean, color: string, key: number) => {
  const lit = on && chip.driver;
  return (
    <span
      key={key}
      style={{
        fontSize: 9.5,
        fontWeight: lit ? 700 : 600,
        color: lit ? color : '#8a837b',
        background: lit ? `${color}1f` : '#8a837b1a',
        borderRadius: 3,
        padding: '1px 5px',
        whiteSpace: 'nowrap',
      }}
    >
      {renderArrows(chip.label, `bp-${key}`)}
    </span>
  );
};

// One Systemic Formulas product line: just the product name, a link to its
// product page (or plain text when there's no URL). No code/note.
const productLine = (name: string, link: string | undefined, on: boolean, key: number) => (
  <li key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 3, fontSize: 10, color: on ? '#5c564f' : '#a49c92', lineHeight: 1.4 }}>
    <span>•</span>
    {link ? (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: on ? '#337ab7' : '#a49c92', textDecoration: 'underline', fontWeight: on ? 600 : 400 }}
      >
        {name}
      </a>
    ) : (
      <span style={{ fontWeight: on ? 600 : 400 }}>{name}</span>
    )}
  </li>
);

export class MvxBlueprintPanel extends React.Component<Props> {
  static shouldRender(ba: BloodworkAnalysis | undefined): boolean {
    // Routing needs the six MVX Plus factors — the basic MVX test (indices only)
    // has nothing to route, so the Blueprint panel stays hidden.
    return analyzeMvx(inputsFromBloodwork(ba)).factors.some(f => f.hasValue);
  }

  // Prefer the supplement catalog's exact URL (the sheet, via supplementLinkByName);
  // fall back to the model's URL for products not yet in the catalog.
  linkFor(p: MvxSupplement): string | undefined {
    return this.props.furtherConsiderations?.supplementLinkByName(p.name) ?? p.link;
  }

  renderBox(step: BlueprintStepResult) {
    const on = step.indicated;
    const color = STEP_COLORS[step.num - 1];
    const supplements = MVX_STEP_SUPPLEMENTS.find(e => e.step === step.num)?.products ?? [];
    return (
      <div
        key={step.num}
        style={{
          flex: '1 1 0',
          minWidth: 0,
          border: `1px solid ${on ? color : '#e4dfd8'}`,
          borderTop: `3px solid ${color}`,
          borderRadius: 6,
          background: on ? `${color}12` : '#f7f6f4',
          padding: '9px 11px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          opacity: on ? 1 : 0.6,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <div
            style={{
              flexShrink: 0,
              width: 21,
              height: 21,
              borderRadius: '50%',
              background: color,
              color: '#fff',
              fontSize: 11,
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {step.num}
          </div>
          <div style={{ fontSize: 12, fontWeight: on ? 800 : 600, color: on ? '#2a2a2a' : '#8a837b', lineHeight: 1.15 }}>
            {step.title}
          </div>
        </div>
        <div style={{ fontSize: 10.5, color: on ? '#5c564f' : '#a49c92', lineHeight: 1.35 }}>{step.focus}</div>
        {step.markers.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
            {step.markers.map((m, i) => markerChip(m, on, color, i))}
          </div>
        )}
        {supplements.length > 0 && (
          <div style={{ borderTop: `1px solid ${on ? `${color}33` : '#eae5de'}`, marginTop: 4, paddingTop: 5 }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: on ? color : '#a49c92', marginBottom: 3 }}>
              Supplements
            </div>
            <ul style={{ listStyle: 'none', margin: 0, paddingLeft: 0 }}>
              {supplements.map((p, i) => productLine(p.name, this.linkFor(p), on, i))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { bloodworkAnalysis } = this.props;
    const result = analyzeMvx(inputsFromBloodwork(bloodworkAnalysis));
    if (!result.factors.some(f => f.hasValue)) return null;

    const steps = resolveBlueprintSteps(result.states);
    const indicated = steps.filter(s => s.indicated).length;
    // Step O (Optimization) has no box — its products live in the footer note.
    const stepO = MVX_STEP_SUPPLEMENTS.find(e => e.step === 0)?.products ?? [];

    return (
      <Card className="evaluationPanel" id="mvx-blueprint" style={{ width: 'auto', maxWidth: '100%', marginTop: 20 }}>
        <Card.Header className="evaluationPanelHeading">
          <span>7-Step Blueprint to Optimal Health — (Dr. Darren Schmidt)&nbsp;&nbsp;</span>
          <OverlayTrigger
            placement="right"
            trigger="click"
            rootClose
            overlay={
              <Popover id="mvx-blueprint-tooltip">
                <Popover.Content>
                  <div style={{ textAlign: 'left' }}>
                    Dr. Darren Schmidt&rsquo;s <strong>7-Step Blueprint to Optimal Health</strong> is a general
                    framework for rebuilding health step by step — from foundational diet and cellular energy through
                    drainage, gut and whole-body immune cleansing, and detoxification, with optimization throughout. The
                    MVX markers point to the {indicated} highlighted step{indicated === 1 ? '' : 's'} — where to focus first.
                    <div style={{ marginTop: 6 }}>
                      <a href="https://www.thenutritionalhealingcenter.com/" target="_blank" rel="noopener noreferrer">
                        Learn more
                      </a>
                    </div>
                  </div>
                </Popover.Content>
              </Popover>
            }
          >
            <FontAwesomeIcon icon="info-circle" style={{ color: '#fff', cursor: 'pointer' }} />
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: 10, alignItems: 'stretch' }}>
            {steps.map(s => this.renderBox(s))}
          </div>
          <div style={{ borderTop: '1px solid #eee7df', marginTop: 12, paddingTop: 8, fontSize: 9.5, color: '#a49c92', lineHeight: 1.5 }}>
            Step order matters — drainage before detox, extracellular before intracellular. Step&nbsp;O — Optimization (
            {stepO.map((p, i) => {
              const link = this.linkFor(p);
              return (
                <React.Fragment key={i}>
                  {i > 0 && ', '}
                  {link ? (
                    <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: '#337ab7', textDecoration: 'underline' }}>
                      {p.name}
                    </a>
                  ) : (
                    p.name
                  )}
                </React.Fragment>
              );
            })}
            ) runs throughout. Retest MVX monthly during intensive work, then quarterly — the score is the report card.
          </div>
        </Card.Body>
      </Card>
    );
  }
}

export default MvxBlueprintPanel;
